import { logger } from "@/utils/debug";

export interface AsyncLoaderConfig<T, R, S> {
  loadFn: () => Promise<T>;
  dataKey: keyof S;
  loadingKey: keyof S;
  promiseKey: keyof S;
  actionName: string;
  transform?: (data: T) => R;
  onError?: (error: unknown) => R | void;
}

type SetState<S> = (
  partial: Partial<S> | ((state: S) => Partial<S>),
  replace?: false,
  action?: string
) => void;

export function createAsyncLoader<T, R = T, S = Record<string, unknown>>(
  config: AsyncLoaderConfig<T, R, S>
) {
  return (set: SetState<S>, get: () => S): (() => Promise<void>) => {
    return async () => {
      const state = get();
      const data = state[config.dataKey] as R[];

      if (Array.isArray(data) && data.length > 0) {
        return;
      }

      const promise = state[config.promiseKey] as Promise<R> | null;
      if (promise) {
        await promise;
        return;
      }

      const loadPromise = (async () => {
        try {
          set(
            { [config.loadingKey]: true } as Partial<S>,
            false,
            `${config.actionName}-start`
          );

          const loadedData = await config.loadFn();
          const result: R = config.transform
            ? config.transform(loadedData)
            : (loadedData as unknown as R);

          set(
            {
              [config.dataKey]: result,
              [config.loadingKey]: false,
              [config.promiseKey]: null,
            } as Partial<S>,
            false,
            `${config.actionName}-success`
          );

          return result;
        } catch (error) {
          logger.error(`Failed to load ${config.actionName}:`, error);

          const errorResult = config.onError?.(error);
          const finalResult =
            errorResult !== undefined ? errorResult : ([] as R);

          set(
            {
              [config.loadingKey]: false,
              [config.promiseKey]: null,
              ...(errorResult !== undefined && {
                [config.dataKey]: finalResult,
              }),
            } as Partial<S>,
            false,
            `${config.actionName}-error`
          );

          if (errorResult === undefined) {
            throw error;
          }

          return finalResult;
        }
      })();

      set(
        { [config.promiseKey]: loadPromise } as Partial<S>,
        false,
        `${config.actionName}-promise`
      );

      await loadPromise;
    };
  };
}
